import {
  FlightExplorerPage,
  type FlightExplorerPageProps,
  type FlightExplorerOffer,
  type FlightExplorerSearchSlots,
  type FlightPinControl as KitFlightPinControl,
  type FlightSearchFn,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n, type GenUiKey } from '../../../shared/ui/i18n.js';
import { useFlightExplorerEnv } from '../flight_results_env.js';
import { ExplorerPill } from './explorer_pill.js';
import { ExplorerPlannerOverlay } from './explorer_planner_overlay.js';
import { SearchBand } from './search_band.js';

/** A superfície pinada pelos grupos J/K — NÃO renomear props sem tocar lá. */
export interface FlightExplorerProps extends Omit<
  FlightExplorerPageProps,
  'env' | 'search' | 'slots' | 'faresCtaLabel'
> {
  /** O host tira a faixa de busca da coluna de resultados (#2113): o contexto e
   *  a edição da busca vivem na pill do topo do SHELL, que é dele — a bancada
   *  passa `true`. */
  hideSearchBand?: boolean;
  /** A faixa de busca vira PILL + planner inline (paridade com a bancada,
   *  #2129/#2130): a casca do chat passa `true` — a barra própria dela é onde a
   *  pill mora. */
  bandAsPill?: boolean;
  /** Seam de busca. A bancada injeta por prop; ausente cai pro
   *  `GenUiConfig.flightSearch` (a casca do chat). Sem NENHUM dos dois o
   *  explorer não monta. */
  search?: FlightSearchFn;
  /** O verbo do CTA da tela de tarifas no pick FINAL, pela chave do catálogo.
   *  Ausente ⇒ "Ir para checkout". */
  faresCtaLabel?: GenUiKey;
  /** Onde a fila de chips monta no palco estreito quando não é o slot do
   *  `GenUiConfig`: a segunda linha da barra da tela em diálogo da conversa. */
  toolbarSlot?: HTMLElement | null;
}

/** O planner da conversa nos slots de busca do kit: a faixa na coluna, ou a
 *  pill na barra com o planner que ela expande. */
function searchSlotsOf(
  hideSearchBand: boolean | undefined,
  bandAsPill: boolean | undefined,
): FlightExplorerSearchSlots {
  if (bandAsPill) {
    return {
      pill: (props) => <ExplorerPill {...props} />,
      planner: (props) => <ExplorerPlannerOverlay {...props} />,
    };
  }
  if (hideSearchBand) return {};
  return { band: (props) => <SearchBand {...props} /> };
}

/**
 * O explorer do kit (#4997) ligado ao que só o genui-kit tem: a busca do
 * `GenUiConfig` como fallback, os textos do catálogo, o cadastro e o
 * classificador do domain, as portas de tarifa, detalhe, pedido offline e
 * ditado, a telemetria e o planner da conversa nos slots de busca.
 */
export function FlightExplorer({
  hideSearchBand,
  bandAsPill,
  search,
  faresCtaLabel,
  toolbarSlot,
  ...props
}: Readonly<FlightExplorerProps>) {
  const { t } = useGenUiI18n();
  const { flightSearch } = useGenUiConfig();
  const env = useFlightExplorerEnv();
  // Prop vence; o config é o fallback (a casca do chat).
  const fs = search ?? flightSearch;
  if (!fs) return null;
  return (
    <FlightExplorerPage
      {...props}
      search={fs}
      env={toolbarSlot === undefined ? env : { ...env, toolbarSlot }}
      slots={searchSlotsOf(hideSearchBand, bandAsPill)}
      faresCtaLabel={faresCtaLabel ? t(faresCtaLabel) : undefined}
    />
  );
}

/** O pino "comprar depois" (#2294) sobre as ofertas do explorer. */
export type FlightPinControl = KitFlightPinControl<FlightExplorerOffer>;
